// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Decides what — if anything — the profile screen should send.
 *
 * The screen has no Save button: every field commits on its own, the way the toggles further down
 * the screen already did. That makes the decision run on every blur and every chip tap rather than
 * once per deliberate press, so it lives here as a pure function that can be tested without
 * mounting the screen.
 *
 * Three outcomes, and the difference between the last two matters: `invalid` is a half-finished
 * edit the user should be told about, `clean` is the common case of a commit that has nothing new
 * to say and must stay silent.
 */

import { parseDecimalInput } from '@/utils/decimalInput';

export type SearchVisibility = 'name' | 'golfId' | 'never';

export const searchVisibilityOptions: SearchVisibility[] = ['name', 'golfId', 'never'];

export type HomeClubProfile = {
  clubId: string;
  clubName: string;
  clubCity?: string | null;
  isPrimary?: boolean;
};

export type ProfileUpdatePayload = {
  displayName: string;
  email: string;
  gender: string;
  handicap: number | null;
  handicapDate: string | null;
  golfId: string;
  searchVisibility: SearchVisibility;
  homeClubs: Array<{
    clubId: string;
    clubName: string;
    clubCity: string | null;
    isPrimary: boolean;
  }>;
};

export type ProfileFormFields = {
  displayName: string;
  email: string;
  gender: string;
  handicap: string;
  handicapDate: string;
  golfId: string;
  searchVisibility: SearchVisibility;
  homeClubs: HomeClubProfile[];
};

export type ProfileUpdateDecision =
  /** A half-finished edit. `messageKey` is an existing i18n key; nothing is sent. */
  | { status: 'invalid'; messageKey: string }
  /** Nothing to send: either unchanged, or identical to the update already in flight. */
  | { status: 'clean' }
  | { status: 'ready'; payload: ProfileUpdatePayload };

export const golfIdMaxLength = 32;

/**
 * Mirrors the loose server-side rule: any national federation format is fine, but the value has to
 * look like an identifier rather than free text.
 */
export function isValidGolfId(value: string): boolean {
  if (value.length === 0) {
    return true;
  }
  if (value.length > golfIdMaxLength) {
    return false;
  }
  if (!/[\p{L}\p{N}]/u.test(value)) {
    return false;
  }
  return /^[\p{L}\p{N}\- ]+$/u.test(value);
}

export function parseSearchVisibility(value: string | null | undefined): SearchVisibility {
  return searchVisibilityOptions.includes(value as SearchVisibility)
    ? (value as SearchVisibility)
    : 'name';
}

/**
 * Trims and orders the clubs so two lists holding the same clubs serialize identically — the
 * primary first, then by id. Without a stable order a plain reordering would read as a change and
 * re-send on every commit.
 */
export function normalizeHomeClubs(items: HomeClubProfile[]): ProfileUpdatePayload['homeClubs'] {
  return items
    .map((item, index) => ({
      clubId: item.clubId,
      clubName: item.clubName?.trim() ?? '',
      clubCity: item.clubCity?.trim() || null,
      isPrimary: item.isPrimary === true,
      _order: index,
    }))
    .sort((a, b) => {
      if (a.isPrimary === b.isPrimary) {
        if (a.clubId === b.clubId) {
          return a._order - b._order;
        }
        return a.clubId.localeCompare(b.clubId);
      }
      return a.isPrimary ? -1 : 1;
    })
    .map(({ _order, ...rest }) => rest);
}

/**
 * `lastSentPayload` is the serialized payload of the most recent send, and is the only thing that
 * keeps repeated commits quiet: the screen's `player` snapshot is refreshed by a later
 * `players_snapshot` broadcast, so comparing against it would re-send the same values until that
 * arrives.
 */
export function buildProfileUpdate(
  fields: ProfileFormFields,
  lastSentPayload: string | null
): ProfileUpdateDecision {
  const handicapText = fields.handicap.trim();
  const handicapDateText = fields.handicapDate.trim();

  if (handicapText && !handicapDateText) {
    return { status: 'invalid', messageKey: 'profile.handicapDateRequired' };
  }
  if (handicapDateText && !handicapText) {
    return { status: 'invalid', messageKey: 'profile.handicapRequired' };
  }

  let handicap: number | null = null;
  if (handicapText) {
    handicap = parseDecimalInput(handicapText);
    if (!Number.isFinite(handicap)) {
      return { status: 'invalid', messageKey: 'profile.invalidHandicap' };
    }
  }

  let handicapDate: string | null = null;
  if (handicapDateText) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(handicapDateText)) {
      return { status: 'invalid', messageKey: 'profile.invalidHandicapDate' };
    }
    handicapDate = handicapDateText;
  }

  const golfId = fields.golfId.trim();
  if (!isValidGolfId(golfId)) {
    return { status: 'invalid', messageKey: 'profile.invalidGolfId' };
  }
  if (fields.searchVisibility === 'golfId' && golfId.length === 0) {
    return { status: 'invalid', messageKey: 'profile.searchVisibilityGolfIdRequired' };
  }

  const payload: ProfileUpdatePayload = {
    displayName: fields.displayName.trim(),
    email: fields.email.trim(),
    gender: fields.gender.trim(),
    handicap,
    handicapDate,
    golfId,
    searchVisibility: fields.searchVisibility,
    homeClubs: normalizeHomeClubs(fields.homeClubs),
  };

  if (lastSentPayload !== null && serializeProfileUpdate(payload) === lastSentPayload) {
    return { status: 'clean' };
  }
  return { status: 'ready', payload };
}

/** The comparison key for `lastSentPayload`. Field order is fixed by `buildProfileUpdate`. */
export function serializeProfileUpdate(payload: ProfileUpdatePayload): string {
  return JSON.stringify(payload);
}
